import { StatusCodes } from "http-status-codes"
import { useCallback, useMemo } from "react"
import { toast } from "react-toastify"
import { useDeleteConfirm } from "../../../../../../hooks/useDeleteConfirm"
import { digitalManagerApi } from "../../../../../../services/api"
import { ApplicationPropertyDetail } from "../../../../../models/ApplicationProperty"
import { ApplicationRoleUserGet } from "../../../../../models/ApplicationRoleUser"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../models/Authorization"
import { UserInfo } from "../../../../../models/User"
import { DiMaAddButton } from "../../../../partials/DiMaAddButton"
import { EntityRow, EntityTab } from "../../../../partials/EntityTab"

export function UserApplicationRoleProperties({
  userApplicationRole,
  reloadUserApplicationRole,
  user,
}: {
  readonly userApplicationRole: ApplicationRoleUserGet
  readonly reloadUserApplicationRole: () => void
  readonly user: UserInfo
}) {
  const { requestDelete, DeleteConfirmDialog } = useDeleteConfirm()

  const canDelete = hasUserAccessToApplicationFeature(
    user.authorizations.applications,
    userApplicationRole.application.id,
    DimaFeatureShortNameEnum.RM,
    AccessLevel.Write
  )

  const onDelete = useCallback(
    (row: EntityRow) => {
      requestDelete(() => {
        digitalManagerApi
          .delete(
            `/applications/${userApplicationRole.application.id}/properties/${row.name}/values/${row.value}`,
            {
              params: {
                userId: userApplicationRole.user.id,
                roleId: userApplicationRole.role.id,
              },
            }
          )
          .then((res) => {
            if (res.status === StatusCodes.OK) {
              toast.success("Property deleted")
            }
          })
          .catch(() => toast.error("Cannot delete the property"))
          .finally(() => reloadUserApplicationRole())
      })
    },
    [
      reloadUserApplicationRole,
      requestDelete,
      userApplicationRole.application.id,
      userApplicationRole.role.id,
      userApplicationRole.user.id,
    ]
  )

  const cols = [
    { field: "name", headerName: "Name" },
    { field: "label", headerName: "Value" },
  ]

  const rows: EntityRow[] = userApplicationRole.properties.map(
    (property: ApplicationPropertyDetail, index: number) => ({
      id: index.toString(),
      name: property.name,
      value: property.value,
      label: property.label,
    })
  )

  const toolbar = useMemo(
    () => (
      <DiMaAddButton
        url={`/users/${userApplicationRole.user.id}/applications/${userApplicationRole.application.id}/roles/${userApplicationRole.role.id}/properties/create`}
        feature={DimaFeatureShortNameEnum.RM}
        applicationId={userApplicationRole.application.id}
      />
    ),
    [userApplicationRole]
  )

  const handleActionMenuClick = useCallback(
    (item: { title: string; iconName?: string }, row: EntityRow) => {
      if (item.title === "Delete") {
        onDelete(row)
      }
    },
    [onDelete]
  )

  return (
    <>
      {DeleteConfirmDialog}
      <EntityTab
      rows={rows}
      cols={cols}
      isLoading={false}
      toolbar={toolbar}
      actionItems={
        canDelete ? [{ title: "Delete", iconName: "icon_abb_dias icon-abb_delete" }] : []
      }
      onActionMenuClick={handleActionMenuClick}
    />
    </>
  )
}
